import type React from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {EXPLAINERS, type ExplainerBlock} from '../domain/settingsExplainers';

const Block: React.FC<{block: ExplainerBlock}> = ({block}) => (
    <Stack gap={0.75}>
        <Typography variant="subtitle2">{block.heading}</Typography>
        {block.text && <Typography variant="body2">{block.text}</Typography>}
        {block.bullets && (
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {block.bullets.map(b => <Typography key={b} component="li" variant="body2" sx={{mb: 0.5}}>{b}</Typography>)}
            </Box>
        )}
        {block.table && (
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label={block.heading}>
                    <TableHead>
                        <TableRow>{block.table.columns.map(c => <TableCell key={c}>{c}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {block.table.rows.map(row => (
                            <TableRow key={row[0]}>
                                {row.map((cell, i) => (
                                    <TableCell key={block.table!.columns[i]} sx={{verticalAlign: 'top', ...(i === 0 && {fontWeight: 600, whiteSpace: 'nowrap'})}}>
                                        {cell}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )}
    </Stack>
);

/** How the Category Profile is built (staff only) and how the strategy is set up. */
export const ExplainersSection: React.FC<{staff: boolean}> = ({staff}) => (
    <Paper variant="outlined" id="settings-explainers" sx={{p: 2, scrollMarginTop: 16}}>
        <Typography variant="h6" component="h2">Explainers</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
            How the feature works behind the scenes. Figures are the defaults; most are settings on this page.
        </Typography>
        {EXPLAINERS.filter(e => staff || !e.staffOnly).map(e => (
            <Accordion key={e.key} disableGutters variant="outlined" sx={{'&:not(:last-of-type)': {borderBottom: 0}}}>
                <AccordionSummary expandIcon={<ExpandMore/>} aria-controls={`explainer-${e.key}`} id={`explainer-${e.key}-header`}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>{e.title}</Typography>
                </AccordionSummary>
                <AccordionDetails id={`explainer-${e.key}`}>
                    <Stack gap={2}>
                        <Typography variant="body2">{e.intro}</Typography>
                        {e.blocks.map(b => <Block key={b.heading} block={b}/>)}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        ))}
    </Paper>
);
